import type { AaiRequest, CompanyDashboard, DepartmentAggregate } from '../../types/aai';
import { isReadyToUse } from '../owner/WorkRows';

/*
 * What every AI Champion page agrees on: which request waits on the champion,
 * how the open work is ranked into priorities, and which teams need support.
 * The champion clears the final gate on a request and runs the adoption
 * program; the Owner decides opportunities, quotes and release. Nothing here
 * asserts a value the platform has not recorded.
 */

export const plural = (n: number, one: string, many: string): string => {
  if (n === 1) return `${n} ${one}`;
  return `${n} ${many}`;
};

export const money = (amount: number | null | undefined, currency: string | null | undefined): string => {
  if (amount === null || amount === undefined) return 'None yet';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD', maximumFractionDigits: 0 }).format(amount);
  } catch {
    return `${amount}`;
  }
};

export const quarterLabel = (): string => `Q${Math.floor(new Date().getMonth() / 3) + 1}`;

export const isOpen = (r: AaiRequest): boolean => r.status !== 'declined';

/** The final gate: cleared by a department head, or raised with no department to clear it. */
export const needsChampion = (r: AaiRequest): boolean => {
  // The server decides from the same routing the decision uses, including a
  // department with no head, whose requests come straight to the champion.
  if (typeof r.needs_you === 'boolean') return r.needs_you;
  if (r.source === 'orbit_problem') return false;
  if (r.status === 'dept_approved') return true;
  return r.status === 'pending' && !r.department;
};

export type WorkKind = 'Quick Win' | 'Delivery Project';

export const kindOf = (r: AaiRequest): WorkKind => {
  if (isReadyToUse(r)) return 'Quick Win';
  return 'Delivery Project';
};

const RISK_WORD: Record<string, string> = { high: 'High', medium: 'Review', low: 'Low' };

export const riskWord = (r: AaiRequest): string => RISK_WORD[(r.risk || '').toLowerCase()] || 'Review';

export const isMaterialRisk = (r: AaiRequest): boolean => (r.risk || '').toLowerCase() === 'high';

/** Upper bound of the recorded forecast value band, or null when no forecast is recorded. */
export const forecastHigh = (r: AaiRequest): number | null => {
  const band = parseBand(r.est_value_band);
  if (!band) return null;
  return band.high;
};

const createdAt = (r: AaiRequest): number => {
  if (!r.created_at) return 0;
  return new Date(r.created_at).getTime();
};

const compareForecast = (a: AaiRequest, b: AaiRequest): number => {
  const fa = forecastHigh(a);
  const fb = forecastHigh(b);
  if (fa === null && fb === null) return 0;
  if (fa === null) return 1;
  if (fb === null) return -1;
  return fb - fa;
};

/**
 * Open work ordered for the portfolio: what still needs a decision first, then
 * the largest recorded forecast value, then material risk (it needs the
 * executive's attention sooner, never later), then recency as the weakest
 * tiebreaker. Four readable rules, no weights.
 */
export const rankRequests = (requests: AaiRequest[]): AaiRequest[] => requests
  .filter(isOpen)
  .sort((a, b) => {
    if (needsChampion(a) !== needsChampion(b)) {
      if (needsChampion(a)) return -1;
      return 1;
    }
    const forecast = compareForecast(a, b);
    if (forecast !== 0) return forecast;
    if (isMaterialRisk(a) !== isMaterialRisk(b)) {
      if (isMaterialRisk(a)) return -1;
      return 1;
    }
    return createdAt(b) - createdAt(a);
  });

export interface Related { requests: number; teams: number }

export const relatedTo = (r: AaiRequest, all: AaiRequest[]): Related => {
  let same = [r];
  if (r.capability_tag) same = all.filter((x) => isOpen(x) && x.capability_tag === r.capability_tag);
  const teams = new Set(same.map((x) => x.department).filter(Boolean)).size;
  return { requests: same.length, teams };
};

export const evidenceLine = (r: AaiRequest, all: AaiRequest[]): string => {
  const related = relatedTo(r, all);
  if (related.requests === 1) return 'One request';
  if (related.teams > 1) return `${plural(related.requests, 'related request', 'related requests')} across ${related.teams} teams`;
  return plural(related.requests, 'related request', 'related requests');
};

export const controlLine = (r: AaiRequest): string => {
  const hil = (r.human_in_loop || '').trim();
  if (hil && !/unset/i.test(hil)) return hil;
  if ((r.risk || '').toLowerCase() === 'high') return 'A named person approves every output before release';
  return 'A person reviews every output';
};

export const valueLine = (r: AaiRequest): string => {
  if (r.est_value_band) return `${r.est_value_band} expected yearly value`;
  return 'Forecast not recorded';
};

export const BAND_LABEL: Record<string, string> = {
  nascent: 'Starting out',
  early: 'Early',
  developing: 'Developing',
  mature: 'Mature',
  advanced: 'Advanced',
};

export const bandLabel = (band: string | null | undefined): string => {
  if (!band) return 'Not scored';
  return BAND_LABEL[band] || band;
};

export const isProtected = (d: DepartmentAggregate): boolean => d.suppressed || Boolean(d.maturity?.suppressed);

/** A team whose measured readiness sits under the midpoint needs first-use support before its Project lands. */
export const NEEDS_SUPPORT_BELOW = 50;

export const teamsNeedingSupport = (dashboard: CompanyDashboard | null): DepartmentAggregate[] => {
  if (!dashboard) return [];
  return (dashboard.departments || [])
    .filter((d) => !isProtected(d) && (d.maturity?.maturity_pct ?? 0) < NEEDS_SUPPORT_BELOW)
    .sort((a, b) => (a.maturity?.maturity_pct ?? 0) - (b.maturity?.maturity_pct ?? 0));
};

export interface Coverage { enrolled: number; onboarded: number; pct: number | null }

export const coverageOf = (dashboard: CompanyDashboard | null): Coverage => {
  if (!dashboard) return { enrolled: 0, onboarded: 0, pct: null };
  const enrolled = dashboard.enrolled || 0;
  const onboarded = dashboard.company.onboarded_count || 0;
  if (enrolled === 0) return { enrolled, onboarded, pct: null };
  return { enrolled, onboarded, pct: Math.round((onboarded / enrolled) * 100) };
};

/** The Project a team is getting ready for: its approved work first, then anything open. */
export const linkedWorkFor = (department: string, requests: AaiRequest[]): AaiRequest | null => {
  const mine = requests.filter((r) => isOpen(r) && (r.department || '').toLowerCase() === department.toLowerCase());
  const approved = mine.find((r) => r.status === 'approved');
  if (approved) return approved;
  return mine[0] || null;
};

export const openSettings = (section: string): void => {
  window.dispatchEvent(new CustomEvent('settings:open', { detail: { section } }));
};

const UNIT: Record<string, number> = { k: 1e3, m: 1e6, b: 1e9 };

/** The numbers in a recorded band such as "$40k–$60k / year". Null when none is recorded. */
export const parseBand = (band: string | null | undefined): { low: number; high: number } | null => {
  if (!band) return null;
  const values = Array.from(band.matchAll(/(\d+(?:\.\d+)?)\s*([kKmMbB])?(?![\w%])/g))
    .map((m) => parseFloat(m[1]) * (UNIT[(m[2] || '').toLowerCase()] || 1))
    .filter((v) => Number.isFinite(v) && v > 0);
  if (values.length === 0) return null;
  return { low: Math.min(...values), high: Math.max(...values) };
};

export const compactMoney = (amount: number): string => {
  if (amount >= 1e6) return `$${(amount / 1e6).toFixed(2).replace(/\.?0+$/, '')}M`;
  if (amount >= 1e3) return `$${Math.round(amount / 1e3)}k`;
  return `$${Math.round(amount)}`;
};

export const BLOCKERS = [
  'Checking evidence before first use',
  'Writing the first prompt for defined work',
  'Knowing when to escalate to a person',
  'Understanding which sources are approved',
  'Reviewing a run before sharing the result',
];

export interface Slot { label: string; at: string }

/** The next working-day slots, each with the exact time it names. */
export const slotOptions = (count = 6): Slot[] => {
  const out: Slot[] = [];
  const day = new Date();
  day.setHours(0, 0, 0, 0);
  while (out.length < count) {
    day.setDate(day.getDate() + 1);
    const weekday = day.getDay();
    if (weekday === 0 || weekday === 6) continue;
    const label = day.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
    const morning = new Date(day);
    morning.setHours(10, 0, 0, 0);
    out.push({ label: `${label} · 10:00 AM`, at: morning.toISOString() });
    if (out.length < count) {
      const afternoon = new Date(day);
      afternoon.setHours(14, 0, 0, 0);
      out.push({ label: `${label} · 2:00 PM`, at: afternoon.toISOString() });
    }
  }
  return out;
};

/** A calendar file for one booked slot, so the session can land in any calendar. */
export const calendarFile = (title: string, startIso: string, minutes = 60, description = ''): string => {
  const start = new Date(startIso);
  const end = new Date(start.getTime() + minutes * 60000);
  const stamp = (d: Date): string => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
  const escape = (text: string): string => text.replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/[,;]/g, (m) => `\\${m}`);
  return [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Agents Anywhere//AI Adoption//EN', 'BEGIN:VEVENT',
    `UID:${stamp(start)}-${Math.random().toString(36).slice(2, 8)}@agentsanywhere`,
    `DTSTAMP:${stamp(new Date())}`, `DTSTART:${stamp(start)}`, `DTEND:${stamp(end)}`,
    `SUMMARY:${escape(title)}`, `DESCRIPTION:${escape(description)}`, 'END:VEVENT', 'END:VCALENDAR',
  ].join('\r\n');
};
